import Button from "@/components/button"
import EndShiftErrorSheet from "@/components/end-shift-error-sheet"
import EndShiftSheet from "@/components/end-shift-sheet"
import PickerWithSheet, {
  PickerButtonIndicator,
} from "@/components/picker-with-sheet"
import RoundedButton from "@/components/rounded-button"
import Topbar from "@/components/topbar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Icon } from "@/icons/icon"
import { FormParamStatus } from "@/lib/form/types"
import { posthog } from "@/lib/posthog"
import LogoutConfirmSheet from "@/components/logout-confirm-sheet"
import { useDebounceByHash, useDisableBack } from "@/lib/utils"
import AddDeptSheet from "@/packaging/components/add-dept-sheet"
import { DeptToggleGroup } from "@/packaging/components/dept-toggle-group"
import PackagingForm from "@/packaging/components/packaging-form"
import PendingSubmissionSheet from "@/packaging/components/pending-submission-sheet"
import { MachineFormData } from "@/packaging/lib/form"
import { handleNetworkError, handleOfflineActionError } from "@/services/base"
import {
  discardPackagingShift,
  endPackagingShift,
  useSubmitPackagingForms,
} from "@/services/packaging"
import { getShiftStatus, setSession } from "@/services/session"
import useStore from "@/store"
import { MachineOption, PackagingSheets } from "@/store/packaging"
import { ShiftStatus } from "@/store/session"
import { LabelValue } from "@/types/common"
import { HTTPError } from "ky"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"

export default function PackagingPage() {
  const { currDeptId, setCurrDeptId, ...packagingStore } =
    useStore.use.packaging()
  const sessionStore = useStore.use.session()
  const processSession = useStore.use.packaging().shift
  const userStore = useStore.use.user()
  const navigate = useNavigate()
  useDisableBack()
  const debounce = useDebounceByHash(500)

  const machines = useMachines()
  const depts = useDepts()
  const canEndShift = useCanEndShift()

  const [showLogoutSheet, setShowLogoutSheet] = useState(false)

  useInit()
  useIndicators()

  function useInit() {
    useEffect(() => { }, [])
    useSubmitPackagingForms()
  }

  function useMachines() {
    const [machines, setMachines] = useState<MachineOption[]>([])

    useEffect(() => {
      const form = packagingStore.formSchemas.find(
        (item) => item.id === currDeptId
      )

      if (!form) {
        setMachines([])

        return
      }

      setMachines(
        form.machines.map((item) => ({
          id: item.id,
          label: item.name,
          value: item.id,
        }))
      )
    }, [currDeptId])

    return machines
  }

  function useDepts() {
    const [depts, setDepts] = useState<LabelValue[]>([])

    useEffect(() => {
      const newDepts = packagingStore.formSchemas.map((form) => {
        return {
          id: form.id,
          label: form.name,
          value: form.id,
        }
      })
      setDepts(newDepts)

      if (!currDeptId) {
        setCurrDeptId(newDepts.at(-1)?.value || "")
      }
    }, [packagingStore.formSchemas])

    return depts
  }

  function useIndicators() {
    function getUpdatedIndicators(
      forms: MachineFormData[]
    ): Record<string, PickerButtonIndicator> {
      const result: Record<string, PickerButtonIndicator> = {}

      for (const form of forms) {
        const deptId = form.deptId
        const machineId = form.id

        for (const product of form.products) {
          let calculated = false

          for (const section of product.sections) {
            for (const param of section.parameters) {
              if (param.status !== FormParamStatus.SUBMITTED) {
                calculated = true

                result[`${deptId}.${machineId}.${product.id}`] =
                  PickerButtonIndicator.Warning
                result[`${deptId}.${machineId}`] = PickerButtonIndicator.Warning

                break
              }
            }

            if (calculated) {
              break
            }

            result[`${deptId}.${machineId}.${product.id}`] =
              PickerButtonIndicator.Success
            result[`${deptId}.${machineId}`] = PickerButtonIndicator.Success
          }
        }
      }

      return result
    }

    useEffect(() => {
      debounce("updateIndicators", () => {
        const updated = getUpdatedIndicators(packagingStore.forms)
        packagingStore.setIndicators(updated)
      })
    }, [packagingStore.forms])
  }

  function useCanEndShift() {
    const [canEnd, setCanEnd] = useState(false)

    useEffect(() => {
      for (const form of packagingStore.forms) {
        for (const product of form.products) {
          for (const section of product.sections) {
            if (section.status === FormParamStatus.SUBMITTED) {
              setCanEnd(true)

              return
            }
          }
        }
      }

      setCanEnd(false)
    }, [packagingStore.forms])

    return canEnd
  }

  function handleChangeDept(value: string) {
    setCurrDeptId(value)
  }

  function openAddDeptSheet() {
    packagingStore.setCurrSheet(PackagingSheets.AddDept)
  }

  function canEdit(): boolean {
    const process = sessionStore.session.processes.find(
      (proc) => proc.processId === processSession.processId
    )
    if (!process) {
      return false
    }

    return (
      process.status === ShiftStatus.Started ||
      process.status === ShiftStatus.Draft
    )
  }

  function handleChangeMachine(machineId: string) {
    packagingStore.setCurrMachineId(machineId)
  }

  function handleTriggerDiscardShift() {
    packagingStore.setCurrSheet(PackagingSheets.DiscardShift)
  }

  async function handleDiscardShift() {
    try {
      await discardPackagingShift()
      await navigate("/manage-processes")
    } catch (err) {
      posthog.captureException(err)
      if (handleOfflineActionError(err, { action: "discard", target: "shift" })) {
        return
      }

      const handled = await handleNetworkError(err, navigate)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      console.error(err)
      toast.error("Error discarding shift")
    }
  }

  async function handleEndShift() {
    try {
      if (packagingStore.submissions.length) {
        packagingStore.setCurrSheet(PackagingSheets.EndShiftLoading)

        return
      }

      await endPackagingShift()
      closeAllSheets()
      sessionStore.endProcess(processSession.processId)
      packagingStore.removePendingSheets()
      await navigate("/shift-ended")
    } catch (err) {
      closeAllSheets()
      if (handleOfflineActionError(err, { action: "end", target: "shift" })) {
        return
      }

      const handled = await handleNetworkError(err, navigate)
      posthog.captureException(err)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      toast.error("Error ending shift. Please contact support")
    }
  }

  function triggerEndShift() {
    const indicator = Object.values(packagingStore.indicators).reduce(
      (acc, curr) => {
        if (acc === PickerButtonIndicator.Warning) {
          return PickerButtonIndicator.Warning
        }

        return curr
      },
      PickerButtonIndicator.Success as PickerButtonIndicator
    )

    if (indicator === PickerButtonIndicator.Warning) {
      packagingStore.setCurrSheet(PackagingSheets.EndShiftError)

      return
    }

    packagingStore.setCurrSheet(PackagingSheets.ConfirmEndShift)
  }

  function closeAllSheets() {
    packagingStore.setCurrSheet(PackagingSheets.None)
  }

  async function handleClickPrevHistory() {
    await navigate("/packaging/history")
  }

  async function handleClickBack() {
     void getShiftStatus().then((res) => {
      if (res.shiftSession) {
        setSession(res.shiftSession)
      }
    })

    await navigate("/manage-processes")
  }

  return (
    <div className="bg-backdrop h-dvh overflow-auto">
      <Topbar
        className="sticky top-0 z-10"
        title="Packaging"
        subtitle={userStore.plant.name}
        showBack
        onClickBack={handleClickBack}
      >
        <div className="flex items-center gap-5">
          {!!packagingStore.formSchemas.length && canEdit() && (
            <RoundedButton
              icon={<Icon name="Plus" />}
              onClick={openAddDeptSheet}
            >
              Department
            </RoundedButton>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Icon name="MoreVertical" className="text-button-black h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent inPortal>
              <DropdownMenuItem onClick={handleClickPrevHistory}>
                View Previous History
              </DropdownMenuItem>

              {canEdit() && (
                <>
                  {canEndShift && (
                    <DropdownMenuItem onClick={triggerEndShift}>
                      End Process
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleTriggerDiscardShift}>
                    Discard and Exit
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem onClick={() => setShowLogoutSheet(true)}>
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Topbar>

      <div className="py-4 px-4">
        <DeptToggleGroup
          className="mb-6 mx-auto"
          type="single"
          options={depts}
          defaultValue={depts.at(0)?.value}
          value={currDeptId}
          onValueChange={handleChangeDept}
        />

        {machines.length ? (
          <PickerWithSheet
            className="mb-4"
            options={machines}
            value={packagingStore.currMachineId}
            deptId={currDeptId}
            indicators={packagingStore.indicators}
            onChange={handleChangeMachine}
          />
        ) : (
          <div className="text-center flex flex-col gap-4 items-center justify-center">
            <div className="text-label-text">
              Please select a department or add a new one
            </div>

            {canEdit() && (
              <RoundedButton
                icon={<Icon name="Plus" />}
                onClick={openAddDeptSheet}
              >
                Department
              </RoundedButton>
            )}
          </div>
        )}

        <PackagingForm canEdit={canEdit()} />
      </div>

      <AddDeptSheet />
      <EndShiftSheet
        open={packagingStore.currSheet === PackagingSheets.ConfirmEndShift}
        title="End Process"
        description="You are making the end of your process for today."
        pendingDescription="You are making the end of your process for today. Please select a reason below"
        onClickBack={closeAllSheets}
        onClickConfirm={handleEndShift}
      />
      <EndShiftSheet
        open={packagingStore.currSheet === PackagingSheets.DiscardShift}
        title="Discard Process"
        description="Are you sure you want to Discard and Exit? This will permanently delete your current process data."
        onClickBack={closeAllSheets}
        onClickConfirm={handleDiscardShift}
      />
      <EndShiftErrorSheet
        open={packagingStore.currSheet === PackagingSheets.EndShiftError}
        onClickBack={closeAllSheets}
      />
      <PendingSubmissionSheet
        open={packagingStore.currSheet === PackagingSheets.EndShiftLoading}
        onClickBack={closeAllSheets}
        onEndShift={handleEndShift}
      />
      <LogoutConfirmSheet
        open={showLogoutSheet}
        onOpenChange={setShowLogoutSheet}
      />
    </div>
  )
}
